import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isDefined } from '@ngx-translate/core/src/util';
import { JwtHelper } from 'angular2-jwt';
import { PioneerUtils } from 'app/event/utils/pioneer-utils';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { User, UserType } from '../../auth/models/User';
import { LogService } from '../../shared/log/log.service';
import { MultiAgency } from '../../shared/models/multi-agency';
import { UserPermission } from '../models/user-permission.model';
import { CustomerConfig } from './../../auth/models/Customer-config';
import { AuthenticationHttpService } from './authentication.http.service';
import { UserLocalStorageService } from './user.local.storage.service';


interface DecodedToken {
  jti: string;
  email: string;
  firstname: string;
  lastname: string;
  exp: number;
  hotelInfo: HotelInfo;
  agencyInfo: AgencyInfo;
}

interface HotelInfo {
  allChains: string[];
  allBrands: string[];
  allProperties: string[];
}

interface AgencyInfo {
  allArcIatas: string[];
  customerInfo: string[];
  defaultArcIata: string;
  allCustomers: string[];
}

@Injectable()
export class AuthenticationStore {

  public static readonly TOKEN_NAME = 'id_token';
  private static readonly EXTERNAL_LOGIN = 'external_login';
  private static readonly DEFAULT_AGENCY_STR: string = '_DEFAULT_AGENCY';
  public static readonly CURRENT_AGENCY_STR: string = '_CURRENT_AGENCY';
  public static readonly MULTI_AGENCY_STR = 'MULTI_AGENCY';
  protected jwtHelper: JwtHelper = new JwtHelper();

  private userAuthenticated: ReplaySubject<boolean> = new ReplaySubject(1);
  private userBehaviorSubject: BehaviorSubject<User> = new BehaviorSubject(new User());

  private defaultAgency: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  defaultAgency$: Observable<string> = this.defaultAgency.asObservable();

  private isClientContactSetUp: boolean;
  private clientContactEditable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  clientContactEditable$: Observable<boolean> = this.clientContactEditable.asObservable();

  constructor(
    private authenticationHttpService: AuthenticationHttpService,
    private logService: LogService,
    private userLocalStorageService: UserLocalStorageService,
    private router: Router
  ) {
    this.init();
  }

  protected init(): void {
    this.isClientContactSetUp = false;
    this.clientContactEditable.next(true);
    const encodedToken = this.getToken();
    this.setUserFromToken(encodedToken);
    this.setClientContactEditable(encodedToken);
  }

  get user(): Observable<User> {
    return this.userBehaviorSubject.asObservable();
  }

  login(username: string, password: string): Observable<boolean> {
    const login: Observable<UserPermission> = this.authenticationHttpService.login(username, password);

    return login.map((userPermission: UserPermission) => {
        if (userPermission && userPermission.token) {
          // set token property
          const tokenString = userPermission.token;

          this.logService.debug(`expiration: ${this.jwtHelper.getTokenExpirationDate(tokenString)}`);
          this.logService.debug(`is expired: ${this.jwtHelper.isTokenExpired(tokenString)}`);
          this.logService.debug(`decoded: ${JSON.stringify(this.jwtHelper.decodeToken(tokenString))}`);

          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(false);
          this.updateState();
          this.setUserFromToken(userPermission.token);
          this.userLocalStorageService.resetPagesizes();
          // return true to indicate successful login
          return true;
        } else {
          this.userLocalStorageService.removeUserToken();

          // return false to indicate failed login
          return false;
        }
      }
    );
  }

  refreshAuthentication(): Observable<UserPermission> {
    return this.refreshPermissionsFromToken(this.getToken(), this.isExternalLogin());
  }

  handleTokenFromExternalSource(token: string): Observable<UserPermission> {
    return this.generatePermissionsFromToken(token, true);
  }

  generatePermissionsFromToken(token: string, externalLogin: boolean): Observable<UserPermission> {
    let decodedToken: DecodedToken = null;
    try {
      // Make sure token is in the right format.  This will throw an error if it's not.
      decodedToken = this.jwtHelper.decodeToken(token);
    } catch (error) {
      this.logService.error(`Invalid token format [${token}] with error: ${error}`);
      return Observable.throw(ExternalSourceError.TOKEN_INVALID_FORMAT);
    }

    // Make sure token is not expired
    if (this.jwtHelper.isTokenExpired(token)) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    let agencyId: string = null;
    if (decodedToken && decodedToken.jti) {
      let defaultArcIataJson: string = this.userLocalStorageService.getItem(decodedToken.jti.toUpperCase()
                                                                            + AuthenticationStore.DEFAULT_AGENCY_STR);
      if (defaultArcIataJson != null && defaultArcIataJson.length > 0) {
        let defaultArcIata: MultiAgency = JSON.parse(defaultArcIataJson);
        if (defaultArcIata && defaultArcIata.agencyId
                          && defaultArcIata.agencyId != null
                          && defaultArcIata.agencyId.length > 0) {
          agencyId = defaultArcIata.agencyId;
        }
      }
    }

    return this.authenticationHttpService.getPermissions(token, agencyId)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        this.logService.debug('Returned user permissions');

        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token);

          const newDecodedToken: DecodedToken = this.jwtHelper.decodeToken(userPermission.token);
          if (newDecodedToken && newDecodedToken.agencyInfo
                              && newDecodedToken.agencyInfo.defaultArcIata
                              && newDecodedToken.agencyInfo.defaultArcIata != null) {
            this.userLocalStorageService.setItem(newDecodedToken.jti.toUpperCase() + AuthenticationStore.DEFAULT_AGENCY_STR,
                                                 JSON.stringify(newDecodedToken.agencyInfo.defaultArcIata));
            this.userLocalStorageService.setItem(newDecodedToken.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR,
                                                 JSON.stringify(newDecodedToken.agencyInfo.defaultArcIata));
            this.defaultAgency.next(newDecodedToken.agencyInfo.defaultArcIata);
          }

          if (newDecodedToken && newDecodedToken.agencyInfo) {
            this.authenticationHttpService.retrieveAgencies(userPermission.token).subscribe(
              (data: MultiAgency[]) => {
                if (data && data.length > 1) {
                  let agencies = List<MultiAgency>();
                  data.forEach(agency => {
                    let item = new MultiAgency(
                      {
                        'agencyId': PioneerUtils.safeTrim(agency.agencyId),
                        'agencyName': PioneerUtils.safeTrim(agency.agencyName)
                      }
                    );
                    agencies = agencies.push(item);
                  });
                  this.userLocalStorageService.setItem(AuthenticationStore.MULTI_AGENCY_STR, JSON.stringify(agencies));
                  this.defaultAgency.next(JSON.stringify(agencies));
                }
              },
              (error: ExternalSourceError) => {
              }
            );
          }

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  refreshPermissionsFromToken(token: string, externalLogin: boolean): Observable<UserPermission> {
    let decodedToken: DecodedToken = null;
    try {
      // Make sure token is in the right format.  This will throw an error if it's not.
      decodedToken = this.jwtHelper.decodeToken(token);
    } catch (error) {
      this.logService.error(`Invalid token format [${token}] with error: ${error}`);
      return Observable.throw(ExternalSourceError.TOKEN_INVALID_FORMAT);
    }

    // Make sure token is not expired
    if (this.jwtHelper.isTokenExpired(token)) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.refreshPermissions(token)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        this.logService.debug('Returned user permissions');

        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token);

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  assignDefaultArcIata(token: string, externalLogin: boolean, agencyId: string): Observable<UserPermission> {
    let decodedToken: DecodedToken = null;
    try {
      // Make sure token is in the right format.  This will throw an error if it's not.
      decodedToken = this.jwtHelper.decodeToken(token);
    } catch (error) {
      this.logService.error(`Invalid token format [${token}] with error: ${error}`);
      return Observable.throw(ExternalSourceError.TOKEN_INVALID_FORMAT);
    }

    // Make sure token is not expired
    if (this.jwtHelper.isTokenExpired(token)) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.getPermissions(token, agencyId)
      .catch(error => {
        this.logService.error(`Failed to get permissions`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        this.logService.debug('Returned user permissions');

        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token);

          const newDecodedToken: DecodedToken = this.jwtHelper.decodeToken(userPermission.token);
          if (newDecodedToken && newDecodedToken.agencyInfo
                              && newDecodedToken.agencyInfo.defaultArcIata
                              && newDecodedToken.agencyInfo.defaultArcIata != null) {
            this.userLocalStorageService.setItem(newDecodedToken.jti.toUpperCase() + AuthenticationStore.DEFAULT_AGENCY_STR,
                                                 JSON.stringify(newDecodedToken.agencyInfo.defaultArcIata));
            this.userLocalStorageService.setItem(newDecodedToken.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR,
                                                 JSON.stringify(newDecodedToken.agencyInfo.defaultArcIata));
            this.defaultAgency.next(newDecodedToken.agencyInfo.defaultArcIata);
          }

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  getToken(): string {
    return this.userLocalStorageService.getToken();
  }

  removeToken(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.TOKEN_NAME);
  }

  removeMultiAgency(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.MULTI_AGENCY_STR);
    this.defaultAgency.next(AuthenticationStore.MULTI_AGENCY_STR);
  }

  isExternalLogin(): boolean {
    const loginSourceStr = this.userLocalStorageService.getItem(AuthenticationStore.EXTERNAL_LOGIN);
    return loginSourceStr === 'true';
  }

  setExternalLogin(externalLogin: boolean): void {
    this.userLocalStorageService.setItem(AuthenticationStore.EXTERNAL_LOGIN, externalLogin ? 'true' : 'false');
  }

  removeExternalLogin(): void {
    this.userLocalStorageService.removeItem(AuthenticationStore.EXTERNAL_LOGIN);
  }

  hasPermission(testPermission: string): boolean;
  hasPermission(testPermissions: string[]): boolean;
  hasPermission(arg: string | string[]): boolean {
    let testPermissions: string[];
    if (arg instanceof Array) {
      // arg must be string[]
      testPermissions = arg.map(perm => perm.toLowerCase());
    } else {
      // arg must be a string
      testPermissions = [ arg.toLowerCase() ];
    }

    const userPermissions: string[] = this.userLocalStorageService.getPermissions().map(perm => perm.toLowerCase());
    this.logService.info(`userPermissions: [${userPermissions}]`);
    if (userPermissions.find((up: string) => isDefined(testPermissions.find(tp => tp === up)))) {
      this.logService.info(`Permission: true`);
      return true;
    } else {
      this.logService.info(`Permission: false`);
      return false;
    }
  }

  private isTokenExpired(): boolean {
    const token = this.userLocalStorageService.getToken();

    if (!token) {
      return true;
    }

    return this.jwtHelper.isTokenExpired(token);
  }

  private isTokenValid(): boolean {
    const isTokenValid = !this.isTokenExpired();

    this.logService.verbose(`isTokenValid: ${isTokenValid}`);
    return isTokenValid;
  }

  /**
   * This method will test to determine if the current token is expired.  It will put those
   * results in the Subject so that listeners will be made aware.
   *
   */
  private updateState() {
    this.userAuthenticated.next(this.isTokenValid());
  }

  isLoggedIn(): Observable<boolean> {
    this.updateState();
    return this.userAuthenticated.asObservable();
  }

  get permissions(): string[] {
    return this.userLocalStorageService.getPermissions();
  }

  logout(routeToLogin = true) {
    this.userLocalStorageService.resetPagesizes();
    this.removeToken();
    this.updateState();
    if (routeToLogin) {
      if (this.isExternalLogin()) {
        this.removeExternalLogin();
        this.router.navigate([ '/external-login-expired'], { queryParams: { 'redirect': true }} );
      } else {
        this.router.navigate([ '/login' ]);
      }
    }
  }

  private setUserFromToken(token: string): void {
    if (!token) {
      return;
    }

    if (!this.isClientContactSetUp) {
      this.setClientContactEditable(token);
    }

    const decodedToken: DecodedToken = this.jwtHelper.decodeToken(token);

    // Set up the user type based on the content of the token
    const userType = decodedToken.hotelInfo ? UserType.HOTEL : (decodedToken.agencyInfo ? UserType.AGENCY : UserType.SUPPORT);

    const user = new User({
      username: decodedToken.jti  || '',
      email: decodedToken.email || '',
      firstName: decodedToken.firstname || '',
      lastName: decodedToken.lastname || '',
      expiration: new Date(decodedToken.exp * 1000),
      allChains: decodedToken.hotelInfo ? decodedToken.hotelInfo.allChains : null,
      allBrands: decodedToken.hotelInfo ? decodedToken.hotelInfo.allBrands : null,
      allProperties: decodedToken.hotelInfo ? decodedToken.hotelInfo.allProperties : null,
      allArcIatas: decodedToken.agencyInfo ? decodedToken.agencyInfo.allArcIatas : null,
      customerInfo: decodedToken.agencyInfo ? decodedToken.agencyInfo.customerInfo : null,
      type: userType
    });
    this.defaultAgency.next(token);
    this.setUserBehaviorSubject(user);
  }

  // marked protected so that MockAuthenticationStore can override as public
  protected setUserBehaviorSubject(user: User) {
    this.userBehaviorSubject.next(user);
  }

  public refreshToken(token: string) {
    this.userLocalStorageService.setToken(token);
    this.setUserFromToken(token);
    this.updateState();
  }

  public isSinglePropertyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.allChains && user.allChains.length === 1
      && user.allBrands && user.allBrands.length === 1
      && user.allProperties && user.allProperties.length === 1);
  }

  public isPropertyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.HOTEL);
  }


  public isAgencyUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.AGENCY);
  }

  public isSupportUser(): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (user.type === UserType.SUPPORT);
  }

  public matchUserType(userType: string): boolean {
    const user =  this.userBehaviorSubject.getValue();
    return (userType === user.type.toString());
  }

  updateIataFromToken(newArcIata: string): Observable<UserPermission> {
    const token = this.getToken();
    const externalLogin = this.isExternalLogin();

    try {
      // Make sure token is in the right format.  This will throw an error if it's not.
      this.jwtHelper.decodeToken(token);
    } catch (error) {
      this.logService.error(`Invalid token format [${token}] with error: ${error}`);
      return Observable.throw(ExternalSourceError.TOKEN_INVALID_FORMAT);
    }

    // Make sure token is not expired
    if (this.jwtHelper.isTokenExpired(token)) {
      this.logService.error(`Token is expired: ${token}`);
      return Observable.throw(ExternalSourceError.TOKEN_EXPIRED);
    }

    return this.authenticationHttpService.updateIataFromToken(token, newArcIata)
      .catch(error => {
        this.logService.error(`Failed to get update the iata from token`);
        return Observable.throw(ExternalSourceError.DEFAULT_ERROR);
      })
      .map(userPermission => {
        this.logService.debug('Returned user permissions - iata updated');

        // Make sure we got a valid response back from the server
        if (userPermission && userPermission.token) {
          // store username and jwt token in local storage to keep user logged in between page refreshes
          this.userLocalStorageService.setUserToken(userPermission);
          this.setExternalLogin(externalLogin);
          this.setUserFromToken(userPermission.token);

          return userPermission;
        } else {
          this.logService.error(`Received invalid permissions`);
          throw new Error('Invalid UserPermission');
        }
      });
  }

  public openDefaultArcIataModalPanel(token: string): boolean {
    let decodedToken: DecodedToken = this.jwtHelper.decodeToken(token);
    if (decodedToken && decodedToken.agencyInfo
      && (!decodedToken.agencyInfo.defaultArcIata || decodedToken.agencyInfo.defaultArcIata == null)) {
      this.userLocalStorageService.removeItem(decodedToken.jti.toUpperCase() + AuthenticationStore.CURRENT_AGENCY_STR);
      return true;
    }
  }

  private setClientContactEditable(token: string) {
    if (token && !this.isClientContactSetUp) {
      const decodedToken: DecodedToken = this.jwtHelper.decodeToken(token);

      if (decodedToken && decodedToken.agencyInfo
        && decodedToken.agencyInfo.allCustomers
        && decodedToken.agencyInfo.allCustomers != null) {
        this.authenticationHttpService.isClientContactEditable(token).subscribe(
          (data: CustomerConfig) => {
            if (data) {
              this.isClientContactSetUp = true;
              this.clientContactEditable.next(data.clientContactEditable);
            }
          },
          (error: ExternalSourceError) => {
          }
        );
      }
    }
  }

}

export enum ExternalSourceError {
  TOKEN_EXPIRED,
  TOKEN_INVALID_FORMAT,
  DEFAULT_ERROR
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/permission/services/authentication.store.ts